import { CheckoutContext } from '@/contexts/CheckoutContext';
import { useCouponDiscount } from '@/hooks/useCouponDiscount';
import useSettings from '@/hooks/useSettings';
import CircleCheckIcon from '@/icons/CircleCheckIcon';
import { formatPrice } from '@/utils/format';
import { getRecurrencePeriodLabel } from '@/utils/payment';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormValues } from '../CheckoutForm';
import { InstallmentsSelector } from '../Payments/InstallmentsSelector';

type Props = {
  index: number;
  defaultChecked?: boolean;
};

const BumpItem: React.FC<Props> = ({ index, defaultChecked }) => {
  const [isChecked, setIsChecked] = useState(defaultChecked);

  const { setValue } = useFormContext();

  const settings = useSettings();

  const { canBePaidInInstallments, hasAnySubscription } = useContext(CheckoutContext);

  const { applyCoupon } = useCouponDiscount();

  const form = useFormContext<FormValues>();

  const bump = useWatch({
    control: form.control,
    name: `bumps.${index}`
  });

  const originalPrice = bump.offer.price;

  const finalPrice = applyCoupon(originalPrice, true);

  const discountPercentage = useMemo(() => {
    if (bump.referencePrice) {
      return ((bump.referencePrice - originalPrice) / bump.referencePrice) * 100;
    }
    return 0;
  }, [bump.referencePrice, originalPrice]);

  const handleCheckedBump = () => {
    const newCheckedValue = !isChecked;
    setIsChecked(newCheckedValue);
    setValue(`bumps.${index}.checked`, newCheckedValue);
  };

  useEffect(() => {
    if (!canBePaidInInstallments) {
      setValue(`bumps.${index}.installments`, 1);
      return;
    }
  }, [canBePaidInInstallments]);

  const getFormattedFinalPrice = () => {
    if (bump.offer.type === 'subscription') {
      return `${formatPrice(finalPrice)} / ${getRecurrencePeriodLabel(
        bump.offer.recurrence_period || 30
      )}`;
    }
    return formatPrice(finalPrice);
  };

  return (
    <div
      style={{
        backgroundColor: isChecked
          ? settings.box.selected.background.color
          : settings.box.unselected.background.color,
        border: `2px ${isChecked ? 'solid' : 'dashed'} ${isChecked ? settings.box.selected.header.background.color : settings.box.unselected.header.background.color}`
      }}
      className={`
        cursor-pointer
        ease-in-out
        duration-150 
        my-1
        overflow-hidden
        rounded-md
        shadow-md
        transition
        w-full
        `}
      onClick={handleCheckedBump}
    >
      <div
        style={{
          backgroundColor: isChecked
            ? (settings.box.selected.header.background.color as string)
            : (settings.box.unselected.header.background.color as string)
        }}
        className={`
          ease-in-out
          flex
          justify-between
          py-2
          px-4
          transition
        `}
      >
        <div className="flex-grow">
          <h1
            style={{
              color: isChecked
                ? settings.box.selected.header.text.color.primary
                : settings.box.unselected.header.text.color.primary
            }}
            className="text-lg xs:text-sm text-white font-semibold line-clamp-3 overflow-hidden text-ellipsis"
          >
            {bump.cta.trim()}
          </h1>
        </div>
        <div className="flex items-center shrink-0 basis-auto">
          {isChecked && (
            <span
              style={{
                color: isChecked
                  ? settings.box.selected.header.text.color.primary
                  : settings.box.unselected.header.text.color.primary
              }}
              className="text-white mr-1 xs:hidden"
            >
              Selecionado
            </span>
          )}
          <CircleCheckIcon
            className={`h-4 w-4 ${isChecked ? 'text-[#38C4AF]' : 'text-slate-500'}`}
          />
        </div>
      </div>

      <div className="flex p-4 xs:p-3">
        <div className="flex flex-row gap-2 items-center w-full xs:flex-col">
          <div className="flex gap-2 w-full">
            {bump.image && bump.showImage && (
              <div className="w-[80px] h-[80px] xs:w-[80px] xs:h-[80px] flex-shrink-0">
                <img
                  src={bump.image}
                  alt="Imagem do produto"
                  className="w-full h-full rounded-md"
                />
              </div>
            )}

            <div className="flex flex-col justify-center w-full gap-1">
              <div className="flex justify-between">
                <div className="flex-1">
                  <p
                    style={{
                      color: isChecked
                        ? settings.box.selected.text.color.primary
                        : settings.box.unselected.text.color.primary
                    }}
                    className="font-bold text-white text-sm line-clamp-3 overflow-hidden text-ellipsis"
                  >
                    {bump.title.trim() || 'Nome do seu produto'}
                  </p>
                </div>
                <div className="min-w-[100px] flex justify-center items-center gap-2 xs:flex-col-reverse">
                  {bump.referencePrice !== null && (
                    <>
                      <span
                        style={{
                          color: isChecked
                            ? settings.box.selected.text.color.secondary
                            : settings.box.unselected.text.color.secondary
                        }}
                        className="text-gray-400 line-through mr-1 xs:text-xs"
                      >
                        {formatPrice(bump.referencePrice)}
                      </span>
                      <span
                        style={{
                          color: settings.box.selected.header.text.color.primary
                        }}
                        className="bg-[#0F7864] text-white text-xs font-bold px-2 py-1 rounded-full xs:px-1 xs:font-semibold"
                      >
                        {Math.round(discountPercentage)}% OFF
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1">
                  <p
                    style={{
                      color: isChecked
                        ? settings.box.selected.text.color.secondary
                        : settings.box.unselected.text.color.secondary
                    }}
                    className="text-white text-sm overflow-hidden"
                  >
                    {bump.description.trim() || 'Nome do seu produto'}
                  </p>
                </div>
                <div className="min-w-[100px] relative sm:text-right">
                  {hasAnySubscription && canBePaidInInstallments ? (
                    <InstallmentsSelector
                      itsNew={false}
                      offerPaid={false}
                      hideLabel
                      fieldName={`bumps.${index}.installments`}
                      calculatedInstallmentsFieldName={`bumps.${index}.calculatedInstallments`}
                      offerId={bump.offer.id}
                      offerType={bump.offer.type}
                      price={finalPrice}
                      recurrencePeriod={bump.offer.recurrence_period || 30}
                      autoCalc={false}
                    />
                  ) : (
                    <span
                      style={{
                        color: settings.text.color.active
                      }}
                      className="text-base font-bold px-2 py-1 rounded-full xs:px-0 xs:font-semibold "
                    >
                      {getFormattedFinalPrice()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          width: '100%',
          height: 30,
          background: isChecked
            ? (settings.box.selected.header.background.color as string)
            : (settings.box.unselected.header.background.color as string),
          display: 'flex',
          alignItems: 'center',
          padding: '0 10px',
          cursor: 'pointer'
        }}
        onClick={() => setIsChecked(!isChecked)}
      >
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => setIsChecked(!isChecked)}
          style={{
            marginRight: '8px',
            cursor: 'pointer',
            accentColor: isChecked ? '#38C4AF' : 'auto'
          }}
        />
        <span
          style={{
            userSelect: 'none',
            fontSize: '14px',
            color: isChecked
              ? settings.box.selected.header.text.color.primary
              : settings.box.unselected.header.text.color.primary,
            fontWeight: 'bold'
          }}
        >
          Adicionar Produto
        </span>
      </div>
    </div>
  );
};

export default BumpItem;
